import { Button as CtvButton } from '@accedo/ctv-button';
import { Image } from '@accedo/ctv-image';
import { Nav } from '@accedo/spatial-nav';
import { KeyCodes } from '@accedo/virtual-key';
import { stylesheet } from 'astroturf';
import { FunctionalComponent } from 'preact';
import { useEffect, useMemo } from 'preact/hooks';
import { PageNavigation } from '##/utils/PageNavigation';
import KeyEventHelper from '##/utils/KeyEventHelper';
import { nav } from '##/init';
import { setNavVisibility } from '##/components/SideNav/SideNav';
import { setLoadingIndicatorVisibility } from '##/components/LoadingIndicator/LoadingIndicator';
import ErrorLogo from '##/assets/images/Error@2x.png';
import {
  ERROR_DESCRIPTION_MAP,
  ERROR_MESSAGES_MAP,
  ErrorPageState,
} from './types';

const styles = stylesheet`
  @import '##/styles/import.scss';
  
  .container {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    align-content: center;
    justify-content: center;
  }
  
  .logo {
    height: pxToVw(182);
    margin-bottom: pxToVh(15);
  }
  
  .message {
    font-size: remify(30);
    margin-bottom: pxToVh(23);
  }
  
  .description {
    font-size: remify(23);
    margin-bottom: pxToVh(35);
  }
  
  .buttons {
    display: flex;
    flex-direction: column;
    
    .button {
      border: pxToVw(1) solid $button-grey;
      height: pxToVw(40);
      width: pxToVw(244);
      border-radius: pxToVw(31);
      padding-bottom: pxToVh(2);
      font-size: remify(21);
      font-weight: 600;
      -webkit-text-stroke: 0;
      margin-bottom: pxToVw(15);
      
      &:global(.current-focus) {
        border: pxToVw(1) solid $light-blue;
        background-color: $light-blue;
        outline: none;
        color: $pure-white;
      }
    }
  }
`;

const goBackButtonId = 'go-back-button';
const backToHomePageId = 'back-to-home-page-button';

export const ErrorPage: FunctionalComponent = () => {
  const state = PageNavigation.getCurrentState<ErrorPageState>();

  const [message, description] = useMemo(() => {
    const error = state && state.error ? state.error : 'GENERIC_ERROR';
    const message = ERROR_MESSAGES_MAP[error];
    const description = ERROR_DESCRIPTION_MAP[error];

    return [message, description];
  }, [state]);

  const backToHomePage = (): void => {
    // clear();
    PageNavigation.navigateTo('/');
  };

  useEffect(() => {
    setNavVisibility?.(false);
    setLoadingIndicatorVisibility?.(false);
    nav.assignFocus(goBackButtonId);

    KeyEventHelper.addKeydownListener(
      (keyName) => keyName === KeyCodes.vKey.LEFT,
      'error-screen-listener',
    );
    return () => {
      setNavVisibility?.(true);
      KeyEventHelper.removeKeydownListener('error-screen-listener');
    };
  }, []);

  return (
    <div className={styles.container}>
      <Image url={ErrorLogo} className={styles.logo} />
      <div className={styles.message}>{message}</div>
      <div className={styles.description}>{description}</div>
      <div className={styles.buttons}>
        <Nav.Vertical>
          <CtvButton
            onClick={PageNavigation.handleGlobalBackKey}
            navId={goBackButtonId}
            className={styles.button}
          >
            Go back
          </CtvButton>
          <CtvButton
            onClick={backToHomePage}
            navId={backToHomePageId}
            className={styles.button}
          >
            Back to Homepage
          </CtvButton>
        </Nav.Vertical>
      </div>
    </div>
  );
};
